import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
	AbstractConfigService,
	AlarisFilesService,
	CONFIG_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { FilesUploadResponse } from '@campaign-portal/namespace/entities/files/specs';
import { AdditionalValidators } from '@helpers/shared/validators';
import { BehaviorSubject, catchError, map, Observable, shareReplay, tap, throwError } from 'rxjs';
import * as XLSX from 'xlsx';

@Injectable({
	providedIn: 'root'
})
export class DnisFilesService extends AlarisFilesService {
	fileData = new BehaviorSubject<string[] | null>(null);
	uploadError = new BehaviorSubject<boolean>(false);

	constructor(http: HttpClient, @Inject(CONFIG_SERVICE_INJECTOR) config: AbstractConfigService) {
		super(http, config);
	}

	override upload(file: File): Observable<FilesUploadResponse> {
		if (!file) {
			this.uploadError.next(true);
			return throwError(() => new Error('File is required'));
		}

		this.uploadError.next(false);

		return this.parseFile(file).pipe(
			map(() => ({
				Success: true,
				Total: 1,
				Data: {
					id: this.generateFileId(),
					name: file.name,
					size: file.size,
					progress: 100
				}
			})),
			catchError((error) => {
				this.uploadError.next(true);
				console.error('File upload error:', error);
				return throwError(() => new Error(`Failed to process file: ${error.message}`));
			})
		);
	}

	private parseFile(file: File): Observable<string[]> {
		return new Observable<string[]>((observer) => {
			if (!file) {
				observer.error(new Error('File is required'));
				return;
			}

			const reader = new FileReader();
			const fileType = this.getFileType(file);

			if (fileType === 'unknown') {
				observer.error(new Error(`Unsupported file type: ${file.type}`));
				return;
			}

			reader.onload = (e: ProgressEvent<FileReader>) => {
				try {
					const content = e.target?.result;
					if (!content) {
						throw new Error('Failed to read file content');
					}
					const parsedData =
						fileType === 'csv' ? this.parseCSV(content as string) : this.parseExcel(content as ArrayBuffer);

					observer.next(parsedData);
					observer.complete();
				} catch (error) {
					observer.error(error);
				}
			};

			reader.onerror = () => {
				observer.error(new Error('Failed to read file'));
			};

			if (fileType === 'csv') {
				reader.readAsText(file, 'UTF-8');
			} else {
				reader.readAsArrayBuffer(file);
			}
		}).pipe(
			tap((data) => {
				this.fileData.next(data);
			}),
			shareReplay(1)
		);
	}

	private parseCSV(csvText: string): string[] {
		try {
			const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
			const values: string[] = [];

			for (const line of lines) {
				const lineValues = line
					.split(',')
					.map((value) => value.trim().replace(/^"|"$/g, ''))
					.filter(Boolean);

				values.push(...lineValues);
			}

			if (values.some((el) => !this.isValidE164(el))) {
				throw new Error();
			}

			return [...new Set(values)];
		} catch (error) {
			this.uploadError.next(true);
			console.error('CSV parsing error:', error);
			throw new Error('Failed to parse CSV file');
		}
	}

	private parseExcel(fileData: ArrayBuffer): string[] {
		try {
			const data = new Uint8Array(fileData);
			const workbook = XLSX.read(data, {
				type: 'array',
				cellDates: true,
				cellText: false
			});

			if (workbook.SheetNames.length === 0) {
				return [];
			}

			const worksheet = workbook.Sheets[workbook.SheetNames[0]];
			const jsonData = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
				defval: '',
				blankrows: false
			});

			const firstColumn = jsonData
				.filter((row: any) => Array.isArray(row) && row.length > 0)
				.map((row: any) => row[0]?.toString().trim())
				.filter((value: string) => value);

			if (firstColumn.some((el) => !this.isValidE164(el))) {
				throw new Error();
			}
			return [...new Set(firstColumn)];
		} catch (error) {
			this.uploadError.next(true);
			console.error('Excel parsing error:', error);
			throw new Error('Failed to parse Excel file');
		}
	}

	private generateFileId(): string {
		return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	}

	private isValidE164(phoneNumber: string): boolean {
		return AdditionalValidators.e164Regex.test(phoneNumber);
	}

	private getFileType(file: File): 'csv' | 'excel' | 'unknown' {
		const fileName = file.name.toLowerCase();
		const fileType = file.type.toLowerCase();

		if (fileName.endsWith('.csv')) {
			return 'csv';
		}

		if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
			return 'excel';
		}

		if (fileType.includes('csv') || fileType === 'text/csv' || fileType === 'text/comma-separated-values') {
			return 'csv';
		}

		if (
			fileType.includes('excel') ||
			fileType.includes('spreadsheet') ||
			fileType === 'application/vnd.ms-excel' ||
			fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
			fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.template'
		) {
			return 'excel';
		}

		return 'unknown';
	}
}
